import type { ReactNode } from "react";
import { useIonRouter } from "@ionic/react";

import { I } from "./icons";

/**
 * The page header (SNIPPETS §1 header, §2 TopBar).
 *
 * When to use: first thing inside every page's content column. With `back` it is a focused
 * page's 56px TopBar — a 44px back button whose label names the parent ("Back to Train"). Without
 * it, it is a tab landing's 64px header, with an optional one-line `sub` such as the date.
 *
 * Back goes through `useIonRouter().push(to, "back")`, the BackBar mechanism: a plain push told
 * the outlet it was a *forward* move and stacked a second copy of the parent.
 */
export function TopBar({
  title,
  sub,
  back,
  action,
}: {
  title: ReactNode;
  /** tab landings only: one line under the title, e.g. "Wed 23 Sep" */
  sub?: ReactNode;
  /** `close`: a close icon instead of the chevron — a flow that is left, not a page gone back
   *  from (the questionnaire, kit §28) */
  back?: { to: string; label?: string; close?: boolean };
  /** an IconButton at the right */
  action?: ReactNode;
}) {
  const router = useIonRouter();
  return (
    <header className={`k-topbar${back ? "" : " k-topbar--landing"}`}>
      {back ? (
        <button
          type="button"
          className="k-iconbtn"
          onClick={() => router.push(back.to, "back")}
          aria-label={back.label ?? "Back"}
        >
          {back.close ? I.close : I["chevron-left"]}
        </button>
      ) : null}
      <div className="k-topbar__txt">
        <h1 className="k-topbar__title">{title}</h1>
        {!back && sub ? <p className="k-topbar__sub">{sub}</p> : null}
      </div>
      {action}
    </header>
  );
}
